/**
 * A higher-order component (HOC) that handles page transitions and state management.
 * This component wraps other components to provide automatic state reset and tab visibility
 * management when navigating between different routes.
 *
 * Features:
 * - Resets the map data state on route changes
 * - Manages tab visibility for different routes (/vehicle-monitoring and /assign-vehicle-view)
 * - Integrates with Redux for state management
 *
 * @param {React.ComponentType<P>} Component - The component to be wrapped
 * @returns {React.FC<P>} A new component with page transition handling
 */

import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setTabVisibility } from '../redux/ActivePageSlice'
import { resetState } from '../redux/MapDataSlice'

interface WithPageTransitionProps {
  policies?: number[]
}

const withPageTransition = <P extends WithPageTransitionProps>(Component: React.ComponentType<P>) => {
  return (props: P) => {
    const location = useLocation()
    const dispatch = useDispatch()

    // Handle route changes and manage state
    useEffect(() => {
      // Reset map data state when route changes
      dispatch(resetState())
      const { pathname } = location

      // Update tab visibility based on current route
      if (pathname === '/vehicle-monitoring') {
        dispatch(setTabVisibility('/vehicle-monitoring'))
      }
      if (pathname === '/assign-vehicle-view') {
        dispatch(setTabVisibility('/assign-vehicle-view'))
      }
      if (pathname === '/live-job' || pathname.startsWith('/live-job/')) {
        dispatch(setTabVisibility('/live-job'))
      }

    }, [location, dispatch])

    return <Component {...props} />
  }
}

export default withPageTransition